import { Link } from "react-router";
import { useI18n } from "../../lib/i18n";
import type { MessageKey } from "../../lib/messages";
import { PageHeader } from "../../ui/kit";
import { AskSupport, WhatsAppSupport } from "../Support";
import { ABOUT, COMPANY, FAQ, PRIVACY, SAFETY, TERMS, type Bi, type InfoPage } from "./content";

/** The information pages, in the order the footer and the account page list them. */
export const INFO_LINKS: { to: string; key: MessageKey }[] = [
  { to: "/help", key: "info.help" },
  { to: "/safety", key: "info.safety" },
  { to: "/about", key: "info.about" },
  { to: "/contact", key: "info.contact" },
  { to: "/terms", key: "info.terms" },
  { to: "/privacy", key: "info.privacy" },
];

function useText() {
  const { locale } = useI18n();
  return (b: Bi) => (locale === "ar" ? b.ar : b.en);
}

function Article({ page, dated }: { page: InfoPage; dated?: boolean }) {
  const { t } = useI18n();
  const tx = useText();
  return (
    <article className="info-page">
      <PageHeader title={tx(page.title)} back />
      <p className="info-intro">{tx(page.intro)}</p>
      {dated && <p className="small muted">{t("info.updated", { d: COMPANY.updated })}</p>}
      {page.sections.map((s, i) => (
        <section key={i}>
          <h2>{tx(s.h)}</h2>
          {s.p.map((p, j) => <p key={j}>{tx(p)}</p>)}
        </section>
      ))}
      <OtherPages />
    </article>
  );
}

export const AboutPage = () => <Article page={ABOUT} />;
export const TermsPage = () => <Article page={TERMS} dated />;
export const PrivacyPage = () => <Article page={PRIVACY} dated />;
export const SafetyPage = () => <Article page={SAFETY} />;

export function HelpPage() {
  const { t } = useI18n();
  const tx = useText();
  return (
    <article className="info-page">
      <PageHeader title={t("info.help")} back />
      <p className="info-intro">{t("info.helpIntro")}</p>
      {FAQ.map((group, i) => (
        <section key={i}>
          <h2>{tx(group.topic)}</h2>
          <div className="faq">
            {group.items.map((f, j) => (
              <details key={j}>
                <summary>{tx(f.q)}</summary>
                <p>{tx(f.a)}</p>
              </details>
            ))}
          </div>
        </section>
      ))}
      <section className="card stack">
        <strong>{t("info.stillNeedHelp")}</strong>
        <Link className="btn primary" to="/contact">{t("info.contact")}</Link>
        <WhatsAppSupport />
      </section>
      <OtherPages />
    </article>
  );
}

export function ContactPage() {
  const { t, locale } = useI18n();
  const address = locale === "ar" ? COMPANY.address.ar : COMPANY.address.en;
  return (
    <article className="info-page">
      <PageHeader title={t("info.contact")} back />
      <p className="info-intro">{t("info.contactIntro")}</p>
      <div className="card stack">
        <ContactRow label={t("info.email")} value={COMPANY.supportEmail} href={`mailto:${COMPANY.supportEmail}`} />
        <ContactRow label={t("info.privacyEmail")} value={COMPANY.privacyEmail} href={`mailto:${COMPANY.privacyEmail}`} />
        <p className="small muted">{t("info.hours")}</p>
      </div>
      <WhatsAppSupport />
      <AskSupport />
      <p className="small muted">{t("info.dealTip")}</p>
      {(COMPANY.commercialRegistration || COMPANY.vatNumber || address) && (
        <div className="card stack small">
          {COMPANY.commercialRegistration && <div>{t("info.cr")}: <span dir="ltr">{COMPANY.commercialRegistration}</span></div>}
          {COMPANY.vatNumber && <div>{t("info.vat")}: <span dir="ltr">{COMPANY.vatNumber}</span></div>}
          {address && <div>{address}</div>}
        </div>
      )}
      <OtherPages />
    </article>
  );
}

function ContactRow({ label, value, href }: { label: string; value: string; href: string }) {
  return (
    <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
      <span className="muted">{label}</span>
      <a href={href} dir="ltr" style={{ fontWeight: 600 }}>{value}</a>
    </div>
  );
}

function OtherPages() {
  const { t } = useI18n();
  return (
    <nav className="info-more" aria-label={t("info.more")}>
      {INFO_LINKS.map((l) => <Link key={l.to} to={l.to}>{t(l.key)}</Link>)}
    </nav>
  );
}

/** Site footer: the information pages and the company's registration details. */
export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="site-footer">
      <nav aria-label={t("info.more")}>
        {INFO_LINKS.map((l) => <Link key={l.to} to={l.to}>{t(l.key)}</Link>)}
      </nav>
      <div className="small muted">
        © {new Date().getFullYear()} {COMPANY.nameEn}
        {COMPANY.commercialRegistration && <> · {t("info.cr")} <span dir="ltr">{COMPANY.commercialRegistration}</span></>}
        {COMPANY.vatNumber && <> · {t("info.vat")} <span dir="ltr">{COMPANY.vatNumber}</span></>}
      </div>
    </footer>
  );
}
